import classNames from 'classnames';
import {
    ACTIVE_LANDING_PAGE_SCREEN_SOURCE,
    PENDING_LANDING_PAGE_SCREEN_SOURCE,
    QUESTIONS_SCREEN_SOURCE,
} from 'components/newOnboardingFlow/config';
import Icon from 'foundations/icon';
import { alpha } from 'foundations/theme';
import { Typography } from 'foundations/typography';
import useTheme from 'hooks/useTheme';
import useWhiteLabelling from 'hooks/useWhitelabelling';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useScrollYPosition } from 'react-use-scroll-position';
import styled from 'styled-components';
import { background } from 'styled-system';
import useDeviceInfo from '../../hooks/useDeviceInfo';
import useLocalStorage from '../../hooks/useLocalStorage';
import { putUserProfileDetails } from '../../store/actions/login';
import LocalizedString from '../../translation/header.json';
import ThirdPartyLogo from '../ThirdPartyCoupon/thirdPartyLogo';
import AppLogo from './AppLogo';
import SideMenuToggle from './SideMenuToggle';
import { HEADER_TEMPLATES, useTemplateHeader } from './templates';

const ENGLISH = { name: 'English', key: 'en' };
const LoginButton = React.lazy(() => import('./LoginButton'));
const SideMenu = React.lazy(() => import('./SideMenu'));
const LangButton = React.lazy(() => import('./LangButton'));
const ProfileButton = React.lazy(() => import('./ProfileButton'));
/**
 * The header is highly customizable due to a rigid structure.
 * It's divided into 3 sections:
 * 1. Left: can contain main nav elements for the current template
 * 2. Title: can contain main title element for the current template
 * 3. Right: can contain secondary nav elements for the current template
 */
export default function Header() {
    const dispatch = useDispatch();
    const headerProps = useSelector((state) => state.layout.header);

    const {
        hidden,
        template,
        showAuth = true,
        onPricing = false,
        featureFlags,
        onThirdParty = false,
        thirdParty = '',
        redirect = true,
        enableLang = false,
        source = 'home page header',
        airmeetData,
    } = headerProps;
    const {
        logoUrl,
        isWhiteLabellingEnabled,
        isWhiteLabellingLoading,
        isAirmeetBrandingRestricted,
    } = useWhiteLabelling({
        logoUrl: airmeetData?.venueData?.logo_url,
    });
    const { isMobile, isTablet } = useDeviceInfo();
    const isMobileOrTablet = isMobile || isTablet;
    const isOnlyMobile = isMobile && !isTablet;

    const headerRef = useRef(null);
    const [
        leftElements,
        contentElements,
        rightElements = [],
        topBarElements = [],
    ] = useTemplateHeader(template, {
        ...headerProps,
        isMobileOrTablet,
        isOnlyMobile,
        isTablet,
    });
    const location = useLocation();
    const user = useSelector((state) => state.auth.user);
    const isSideMenuEnabled = useSelector(
        (state) => state.layout?.sideMenu?.enabled
    );
    const [currentLang, setCurrentLang] = useLocalStorage('lang', ENGLISH);
    useEffect(() => {
        if (location.pathname === '/') {
            if (user && user.hasOwnProperty('id')) {
                if (
                    !user.hasOwnProperty('metadata') ||
                    (user.hasOwnProperty('metadata') && !user.metadata['lang'])
                ) {
                    setCurrentLang(currentLang || ENGLISH);
                    updateUserDetails(currentLang);
                } else if (user.metadata.lang) {
                    setCurrentLang(user.metadata.lang);
                }
            } else {
                setCurrentLang(currentLang || ENGLISH);
            }
        }
    }, [user]);

    if (!isSideMenuEnabled && template === HEADER_TEMPLATES.community) {
        rightElements.length = 0;
    }
    const isOnOnboarding = [
        QUESTIONS_SCREEN_SOURCE,
        PENDING_LANDING_PAGE_SCREEN_SOURCE,
        ACTIVE_LANDING_PAGE_SCREEN_SOURCE,
    ].includes(source);

    if (showAuth) {
        // leftElements.push(<Typography variant={'body1'} color={'#A8A5B6'} className={'ml-md-5 mr-4 hover-item-w'}><a href="#" className={'px-0'} >{LocalizedString[langKey].navbar_left.navmenu[0]}</a></Typography>);
        rightElements.push(<LangButton enable={enableLang} />);
        rightElements.push(
            <ProfileButton localString={LocalizedString['en'].profileMenu} />
        );
        rightElements.push(
            <LoginButton
                localString={LocalizedString['en'].navbar_right.navmenu[0]}
                redirect={redirect}
                source={source}
            />
        );
    }

    const updateUserDetails = async (item) => {
        const payload = {
            metadata: {
                lang: item,
            },
        };
        await dispatch(putUserProfileDetails(payload));
    };

    const scrollY = useScrollYPosition();
    useEffect(() => {
        if (!headerRef.current) {
            return;
        }

        if (
            (scrollY >= 60 || (onPricing && scrollY >= 15)) &&
            source !== 'sign up page'
        ) {
            headerRef.current.classList.add('add-color-scroll');
            if (scrollY >= 750) {
                headerRef.current.classList.add('add-on-scroll');
            } else {
                headerRef.current.classList.remove('add-on-scroll');
            }
        } else {
            headerRef.current.classList.remove('add-color-scroll');
        }
    }, [scrollY]);

    const ContentBlock = !!contentElements
        ? contentElements.map((Component, i) =>
              Component ? (
                  <div key={`header-content-${i}`}>{Component}</div>
              ) : null
          )
        : null;

    const getWindowSize = () => {
        return {
            windowWidth: window?.innerWidth || document.body.clientWidth,
            windowHeight: window?.innerHeight || document.body.clientHeight,
        };
    };

    const [isMobileMenu, setActive] = React.useState(false);

    const { theme, isLightTheme } = useTheme();

    const {
        absolute,
        translucent,
        transparent,
        sticky,
        home_header,
        hasBranding,
        dashboard_header,
        brandLogo,
        mobileScreenWidth = 991,
        permissionWorkflow,
        borderBottom,
        hideLogo,
    } = headerProps;

    return !!hidden ? null : (
        <>
            <MainHeader
                ref={headerRef}
                id='main-header'
                className={classNames(
                    'app-header',
                    'clearfix',
                    { sticky: sticky === true },
                    { absolute: absolute === true },
                    { transparent: transparent === true },
                    { translucent: translucent === true },
                    { home_header: home_header === true },
                    { permission_workflow: permissionWorkflow === true },
                    { has_branding: hasBranding === true },
                    { dashboard_header: dashboard_header === true },
                    { pricing_header: onPricing === true },
                    { third_party: onThirdParty === true },
                    { borderBottom: borderBottom === true }
                )}
            >
                {!!topBarElements
                    ? topBarElements.map((Component, i) =>
                          Component ? (
                              <div key={`header-top-bar-${i}`}>{Component}</div>
                          ) : null
                      )
                    : null}
                <div className='header-main'>
                    <div className='float-left'>
                        {/* Toggle for side menu */}
                        <div className='m-menu d-block d-sm-block d-md-none'>
                            <SideMenuToggle />
                        </div>
                        <nav className='navbar'>
                            <ul
                                className={`${
                                    brandLogo
                                        ? 'no-style main-nav has-brand-logo'
                                        : 'no-style main-nav'
                                }`}
                            >
                                {!hideLogo &&
                                    !featureFlags &&
                                    getWindowSize().windowWidth <=
                                        mobileScreenWidth && (
                                        <li>
                                            {/* This is always visible with the header */}
                                            {/* <AppLogo logoOnly={false} /> */}
                                            {isWhiteLabellingEnabled ||
                                            (isAirmeetBrandingRestricted &&
                                                logoUrl &&
                                                !logoUrl.includes(
                                                    'default_logo_image'
                                                )) ? (
                                                <VenueLogo src={logoUrl} />
                                            ) : isWhiteLabellingLoading ? null : (
                                                <AppLogo
                                                    logoOnly={false}
                                                    isLightTheme={isLightTheme}
                                                    isOnOnboarding={
                                                        isOnOnboarding
                                                    }
                                                />
                                            )}
                                        </li>
                                    )}
                                {!hideLogo &&
                                    !featureFlags &&
                                    !brandLogo &&
                                    getWindowSize().windowWidth >
                                        mobileScreenWidth && (
                                        <li>
                                            {isWhiteLabellingEnabled ||
                                            (isAirmeetBrandingRestricted &&
                                                logoUrl &&
                                                !logoUrl.includes(
                                                    'default_logo_image'
                                                )) ? (
                                                <VenueLogo src={logoUrl} />
                                            ) : isWhiteLabellingLoading ? null : (
                                                <AppLogo
                                                    logoOnly={false}
                                                    isLightTheme={isLightTheme}
                                                    isOnOnboarding={
                                                        isOnOnboarding
                                                    }
                                                />
                                            )}
                                        </li>
                                    )}
                                {onThirdParty &&
                                    getWindowSize().windowWidth >
                                        mobileScreenWidth && (
                                        <>
                                            <Typography
                                                variant='h6'
                                                color={
                                                    theme.colors.ambience[14]
                                                }
                                                condensed={true}
                                                style={{
                                                    marginLeft: '15px',
                                                    marginRight: '15px',
                                                }}
                                            >
                                                &
                                            </Typography>
                                            <li>
                                                <ThirdPartyLogo
                                                    thirdParty={thirdParty}
                                                />
                                            </li>
                                        </>
                                    )}

                                {!!leftElements
                                    ? leftElements.map((Component, i) =>
                                          Component ? (
                                              <li
                                                  key={`header-left-${i}`}
                                                  className={'hide-mobile'}
                                              >
                                                  {Component}
                                              </li>
                                          ) : null
                                      )
                                    : null}
                            </ul>
                        </nav>
                    </div>

                    {/* Large screens  */}
                    <div className='header-sub d-none d-sm-flex'>
                        {ContentBlock}
                    </div>

                    <div className='float-right'>
                        <nav
                            className={classNames('navbar', {
                                'hide-mobile': !isOnOnboarding,
                            })}
                        >
                            <ul className='no-style main-nav'>
                                {rightElements.map((Component, i) =>
                                    Component ? (
                                        <li key={`header-right-${i}`}>
                                            {Component}
                                        </li>
                                    ) : null
                                )}
                            </ul>
                        </nav>
                        {!isOnOnboarding && showAuth && (
                            <div className={'hide-desktop'}>
                                <ToggleMenuButton
                                    onClick={() => {
                                        setActive(
                                            (isMobileMenu) => !isMobileMenu
                                        );
                                    }}
                                >
                                    <Icon
                                        width={24}
                                        height={24}
                                        icon={
                                            isMobileMenu
                                                ? 'icon_close'
                                                : 'icon_hamburger'
                                        }
                                        fill='ambience.0'
                                    />
                                </ToggleMenuButton>
                            </div>
                        )}
                    </div>
                </div>

                {/*  Small screens */}
                {ContentBlock != '' && (
                    <div className='header-sub d-flex d-sm-none'>
                        {ContentBlock}
                    </div>
                )}
            </MainHeader>
            <SideMenu
                enable={enableLang}
                localString={LocalizedString['en']}
                isOpen={isMobileMenu}
                closeMenu={() => setActive((isMobileMenu) => !isMobileMenu)}
            />
        </>
    );
}

const ToggleMenuButton = styled.div`
    padding: 0;
    border: 0;
    background: transparent;
    outline: 0 !important;
    box-shadow: none;
    cursor: pointer;
`;

const VenueLogo = styled.img`
    height: auto;
    max-height: 30px;
`;

const MainHeader = styled.header`
    ${({ theme }) =>
        theme.isLightTheme &&
        `
        background-color: ${theme.colors.white} !important;
    `}
    ${background}
    position: ${({ theme }) => theme.isLightTheme && 'relative !important'};
    box-shadow: ${({ theme }) =>
        theme.isLightTheme
            ? `0px 0px 3.6px ${alpha(theme.colors.black, 0.11)},
                0px 0px 14.4px ${alpha(theme.colors.black, 0.04)}`
            : `0px 2px 3px ${theme.colors.black}`};

    &.permission_workflow {
        background: ${({ theme }) =>
            theme.isLightTheme
                ? theme.colors.white
                : theme.colors.ambience[23]};
        border: ${({ theme }) =>
            !theme.isLightTheme && `1px solid ${theme.colors.ambience[21]}`};
        padding: ${({ theme }) => !theme.isLightTheme && '12px 50px'};
        .best-exp-info-web {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            width: 100%;
            margin: 0;
        }
    }
`;
